import DOMPurify from 'dompurify';
import type { Ticket } from '../lib/types';

export type TicketRow = Ticket & {
  customer?: { id: string; navn: string | null; epost: string } | null;
  assignee?: { id: string; navn: string; avatarUrl?: string | null } | null;
  team?: { id: string; navn: string } | null;
  _count?: { messages: number };
  ulest?: boolean;
  sistAktivitet?: string;
  lastMessage?: {
    id: string;
    opprettet: string;
    type: string;
    authorId?: string | null;
    innholdTekst?: string | null;
  } | null;
};

export type Section = {
  key: string;
  label: string;
  items: TicketRow[];
  collapsible?: boolean;
};

/* ---------- Snippet fra siste melding eller beskrivelse ---------- */
export function getSnippet(ticket: TicketRow): string {
  const msg = ticket.lastMessage?.innholdTekst?.trim();
  if (msg) return msg.slice(0, 120);
  if (ticket.beskrivelse) {
    // Beskrivelse kan inneholde HTML – stripp tags for lesbar ren tekst
    const text = DOMPurify.sanitize(ticket.beskrivelse, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    return text.trim().slice(0, 120);
  }
  return '';
}

/* ---------- Aktivitets-etikett for raden ---------- */
export function activityLabel(ticket: TicketRow): string {
  const lm = ticket.lastMessage;
  if (!lm) return 'Ny sak';
  if (lm.type === 'KUNDE_MELDING') {
    return (ticket._count?.messages ?? 0) <= 1 ? 'Ny henvendelse' : 'Kunde svarte';
  }
  if (lm.type === 'INTERNT_NOTAT') return 'Internt notat';
  if (lm.type === 'KUNDE_SVAR') return 'Svar sendt';
  return 'Ny sak';
}

/* ---------- Lest-vannmerke fra faktisk rendrede meldinger ---------- */
export function renderedWatermark(ticket: {
  opprettet: string;
  messages?: Array<{ type: string; opprettet: string }> | null;
}): number {
  let max = Date.parse(ticket.opprettet);
  for (const m of ticket.messages ?? []) {
    if (m.type === 'SYSTEM') continue;
    const t = Date.parse(m.opprettet);
    if (t > max) max = t;
  }
  return max;
}

/* ---------- Seksjonering: saker som krever oppmerksomhet øverst ---------- */
export function buildSections(tickets: TicketRow[], userId?: string): Section[] {
  const isClosed = (t: TicketRow) => t.status === 'LOST' || t.status === 'LUKKET';
  const byActivity = (a: TicketRow, b: TicketRow) =>
    new Date(b.sistAktivitet ?? b.oppdatert).getTime() -
    new Date(a.sistAktivitet ?? a.oppdatert).getTime();

  const unread: TicketRow[] = [];
  const unassigned: TicketRow[] = [];
  const mine: TicketRow[] = [];
  const others: TicketRow[] = [];
  const closed: TicketRow[] = [];

  for (const t of tickets) {
    if (isClosed(t)) closed.push(t);
    else if (t.ulest) unread.push(t);
    else if (!t.assigneeId) unassigned.push(t);
    else if (t.assigneeId === userId) mine.push(t);
    else others.push(t);
  }

  return [
    { key: 'unread', label: 'Ny aktivitet', items: unread.sort(byActivity) },
    { key: 'unassigned', label: 'Trenger tildeling', items: unassigned.sort(byActivity) },
    { key: 'mine', label: 'Mine aktive', items: mine.sort(byActivity) },
    { key: 'others', label: 'Andre åpne saker', items: others.sort(byActivity) },
    { key: 'closed', label: 'Løst / Lukket', items: closed.sort(byActivity), collapsible: true },
  ];
}
